import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Box, Mail, Lock, User, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, register, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) navigate(user.onboarding_complete ? "/dashboard" : "/onboarding");
  }, [user, navigate]);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success("Welcome back!");
      navigate(u.onboarding_complete ? "/dashboard" : "/onboarding");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Login failed — please check your connection";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(regForm.email, regForm.password, regForm.name);
      toast.success("Welcome to Black Box!");
      navigate("/onboarding");
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Registration failed — please check your connection";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-10 rounded-lg bg-[#00FF88] flex items-center justify-center shadow-lg shadow-[#00FF88]/20">
            <Box className="w-5 h-5 text-black" />
          </div>
          <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope' }}>
            Black Box
          </span>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="w-full bg-white/5 border border-white/10 rounded-xl p-1 mb-8">
            <TabsTrigger
              value="login"
              data-testid="auth-login-tab"
              className="flex-1 rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400"
            >
              Sign In
            </TabsTrigger>
            <TabsTrigger
              value="register"
              data-testid="auth-register-tab"
              className="flex-1 rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-slate-400"
            >
              Create Account
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  data-testid="login-email-input"
                  className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl h-14 pl-12"
                  required
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  data-testid="login-password-input"
                  className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl h-14 pl-12"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full h-14 text-base font-medium shadow-[0_0_20px_rgba(0,255,136,0.3)]"
              >
                {loading ? "Signing in..." : "Sign In"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="text"
                  placeholder="Full name"
                  value={regForm.name}
                  onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                  data-testid="register-name-input"
                  className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl h-14 pl-12"
                  required
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="email"
                  placeholder="Email address"
                  value={regForm.email}
                  onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                  data-testid="register-email-input"
                  className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl h-14 pl-12"
                  required
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="password"
                  placeholder="Password (min 6 characters)"
                  value={regForm.password}
                  onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                  data-testid="register-password-input"
                  className="bg-white/5 border-white/10 focus:border-white/30 text-white placeholder:text-white/30 rounded-xl h-14 pl-12"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit-btn"
                className="w-full bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full h-14 text-base font-medium shadow-[0_0_20px_rgba(0,255,136,0.3)]"
              >
                {loading ? "Creating account..." : "Create Account"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        {/* Divider */}
        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-slate-500 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Google Auth */}
        <Button
          onClick={googleLogin}
          data-testid="google-login-btn"
          className="w-full bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-full h-14 text-base font-medium backdrop-blur-md"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </Button>

        <p className="text-center text-xs text-slate-600 mt-8">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </motion.div>
    </div>
  );
}
